/**
 * Geometria dos gráficos diários v2 (extraída de GoogleAdsV2/lib/chart.ts): área SVG 1000×200 esticada, x no centro
 * de cada coluna (dia), curvas Catmull-Rom amostradas (10 pontos por trecho) e eixos. As séries aceitam `null` (dia sem
 * valor), que vira **lacuna** na curva, nunca 0. Funções puras; os formatadores de eixo ficam em cada tela.
 */

export const VW = 1000;
export const VH = 200;

export type Val = number | null;
export interface SP { x: number; v: number }
export interface Tick { l: string; top: number }

const ok = (x: Val | undefined): x is number => x != null && Number.isFinite(x);

/** x do centro da coluna i (de n) no viewBox. */
export const colX = (i: number, n: number) => ((i + 0.5) / n) * VW;

/** y do valor numa escala [mn, mx] (0 no topo do viewBox). */
export const scaleY = (mn: number, mx: number) => (val: number) => VH * (1 - (val - mn) / ((mx - mn) || 1));

/** Teto "bonito" (meia potência de 10): 0,87 → 0,9 · 1234 → 1500. */
export const niceMax = (x: number) => {
    if (!(x > 0)) return 1;
    const pw = Math.pow(10, Math.floor(Math.log10(x)));
    return (Math.ceil((x / pw) * 2) / 2) * pw;
};

/** 5 marcas do eixo, de mn (base, top 100%) a mx (topo, top 0%). */
export const ticks = (mn: number, mx: number, label: (x: number) => string): Tick[] =>
    [0, 1, 2, 3, 4].map((t) => ({ l: label(mn + ((mx - mn) * t) / 4), top: (1 - t / 4) * 100 }));

/** Mínimo e máximo dos valores finitos de várias séries; null se nenhuma tem valor. */
export const extent = (...series: Val[][]): [number, number] | null => {
    let mn = Infinity, mx = -Infinity;
    for (const s of series) for (const x of s) if (ok(x)) { mn = Math.min(mn, x); mx = Math.max(mx, x); }
    return mn === Infinity ? null : [mn, mx];
};

/** Faixas [início, fim] (inclusivas) de dias consecutivos com valor em todas as séries dadas. */
export const runs = (...series: Val[][]): [number, number][] => {
    const n = Math.max(0, ...series.map((s) => s.length));
    const out: [number, number][] = [];
    let start = -1;
    for (let i = 0; i <= n; i++) {
        const has = i < n && series.every((s) => ok(s[i]));
        if (has && start < 0) start = i;
        if (!has && start >= 0) { out.push([start, i - 1]); start = -1; }
    }
    return out;
};

const crp = (a: number, b: number, c: number, d: number, t: number) =>
    0.5 * (2 * b + (c - a) * t + (2 * a - 5 * b + 4 * c - d) * t * t + (3 * b - a - 3 * c + d) * t * t * t);

/** Catmull-Rom amostrada de uma série sem lacunas (k pontos por trecho); xf dá o x de cada índice. */
export const sample = (vals: number[], xf: (i: number) => number, k = 10): SP[] => {
    const m = vals.length, out: SP[] = [];
    if (m < 2) return m ? [{ x: xf(0), v: vals[0] }] : [];
    for (let i = 0; i < m - 1; i++) {
        for (let s = 0; s < k; s++) {
            const t = s / k;
            out.push({ x: xf(i) + (xf(i + 1) - xf(i)) * t, v: crp(vals[Math.max(0, i - 1)], vals[i], vals[i + 1], vals[Math.min(m - 1, i + 2)], t) });
        }
    }
    out.push({ x: xf(m - 1), v: vals[m - 1] });
    return out;
};

/** Amostra um trecho [a, b] de uma série (todos os valores do trecho existem). */
export const sampleRun = (vals: Val[], [a, b]: [number, number], n: number): SP[] =>
    sample(vals.slice(a, b + 1) as number[], (i) => colX(a + i, n));

export const pts = (s: SP[], y: (v: number) => number) => s.map((q) => `${q.x.toFixed(1)},${y(q.v).toFixed(1)}`);

/** Polilinha de pontos; um ponto só vira um traço de comprimento 0 (aparece como ponto com stroke-linecap round). */
const poly = (p: string[]) => 'M' + p.join(' L') + (p.length === 1 ? ' L' + p[0] : '');

/** Curva suave pelos valores (colunas centradas), com lacuna nos dias sem valor. '' se não há valor. */
export const curve = (vals: Val[], y: (v: number) => number): string =>
    runs(vals).map((r) => poly(pts(sampleRun(vals, r, vals.length), y))).join(' ');

/** Área entre duas sequências de pontos (topo → base invertida). */
export const between = (top: string[], bottom: string[]) => 'M' + top.join(' L') + ' L' + [...bottom].reverse().join(' L') + ' Z';

/** Área sob a curva até a linha `y0` (viewBox), por trecho com valor. */
export const areaToBase = (vals: Val[], y: (v: number) => number, y0: number): string =>
    runs(vals).map((r) => {
        const s = sampleRun(vals, r, vals.length);
        return poly(pts(s, y)) + ` L${s[s.length - 1].x.toFixed(1)},${y0.toFixed(1)} L${s[0].x.toFixed(1)},${y0.toFixed(1)} Z`;
    }).join(' ');

/**
 * Áreas entre duas curvas (mockup `gap`): `above` onde a ≥ b (preenche de a até min(a, b)) e `below` onde b > a.
 * Só nos trechos em que as duas séries têm valor.
 */
export const gapAreas = (a: Val[], b: Val[], y: (v: number) => number): { above: string; below: string } => {
    const n = Math.max(a.length, b.length);
    const above: string[] = [], below: string[] = [];
    for (const r of runs(a, b)) {
        const sa = sampleRun(a, r, n), sb = sampleRun(b, r, n);
        const mn = sa.map((q, i) => ({ x: q.x, v: Math.min(q.v, sb[i].v) }));
        above.push(between(pts(sa, y), pts(mn, y)));
        below.push(between(pts(sb, y), pts(mn, y)));
    }
    return { above: above.join(' '), below: below.join(' ') };
};

/** Áreas acima (pos) e abaixo (neg) de zero até a linha do zero (mockup `cum`), por trecho com valor. */
export const signedAreas = (vals: Val[], y: (v: number) => number): { pos: string; neg: string } => {
    const z = y(0).toFixed(1), pos: string[] = [], neg: string[] = [];
    const close = (s: SP[]) => ` L${s[s.length - 1].x.toFixed(1)},${z} L${s[0].x.toFixed(1)},${z} Z`;
    for (const r of runs(vals)) {
        const s = sampleRun(vals, r, vals.length);
        const p = s.map((q) => ({ x: q.x, v: Math.max(0, q.v) })), m = s.map((q) => ({ x: q.x, v: Math.min(0, q.v) }));
        pos.push(poly(pts(p, y)) + close(s));
        neg.push(poly(pts(m, y)) + close(s));
    }
    return { pos: pos.join(' '), neg: neg.join(' ') };
};

/** Soma acumulada; dia sem valor fica null (lacuna) e não interrompe a soma dos dias seguintes. */
export const cumulative = (vals: Val[]): Val[] => {
    let acc = 0;
    return vals.map((x) => (ok(x) ? (acc += x) : null));
};

/**
 * Dia em que o acumulado ficou positivo de vez (mockup `turn`): primeiro dia ≥ 0 cujo anterior com valor era < 0 (ou
 * o primeiro com valor) e todos os seguintes com valor ≥ 0. -1 se termina negativo ou não há valor.
 */
export const turnIndex = (cum: Val[]): number => {
    const idx = cum.map((x, i) => (ok(x) ? i : -1)).filter((i) => i >= 0);
    if (!idx.length || (cum[idx[idx.length - 1]] as number) < 0) return -1;
    let turn = idx[0];
    for (let j = 1; j < idx.length; j++) if ((cum[idx[j - 1]] as number) < 0) turn = idx[j];
    return turn;
};

/**
 * Participação (%) de cada camada por dia: `per[dia][camada]` → mesma forma somando 100. Dia sem custo
 * (total 0) vira null (lacuna no streamgraph).
 */
export const shareMatrix = (per: number[][]): (number[] | null)[] =>
    per.map((row) => {
        const t = row.reduce((a, b) => a + Math.max(0, b), 0);
        return t > 0 ? row.map((x) => (Math.max(0, x) / t) * 100) : null;
    });

/**
 * Streamgraph 100% (DashboardV2 `dom`): para cada camada, o caminho da área (topo acumulado → base acumulada) e o da
 * divisória (topo), por trecho de dias com custo.
 */
export const streamLayers = (shares: (number[] | null)[], layers: number, y: (v: number) => number): { area: string; top: string }[] => {
    const n = shares.length, out: { area: string; top: string }[] = [];
    let acc: Val[] = shares.map((r) => (r ? 0 : null));
    for (let k = 0; k < layers; k++) {
        const next: Val[] = acc.map((a, i) => (a == null ? null : a + (shares[i] as number[])[k]));
        const area: string[] = [], top: string[] = [];
        for (const r of runs(next)) {
            const clamp = (s: SP[]) => s.map((q) => ({ x: q.x, v: Math.min(100, Math.max(0, q.v)) }));
            const tp = pts(clamp(sampleRun(next, r, n)), y), bt = pts(clamp(sampleRun(acc, r, n)), y);
            area.push(between(tp, bt));
            top.push(poly(tp));
        }
        out.push({ area: area.join(' '), top: top.join(' ') });
        acc = next;
    }
    return out;
};

/* ----------------------------------------------------------- datas */
const WD = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
/** "29/09" */
export const dayLabel = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/** "ter, 29/09/2026" (dia da semana pela data do calendário, sem fuso). */
export const tipDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return `${WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}, ${dayLabel(iso)}/${iso.slice(0, 4)}`;
};

/** Rótulos do eixo x: todos até 10 dias; senão ~8 espaçados + o último (sem encostar no penúltimo). */
export const xLabels = (dates: string[]): { t: string; l: number; i: number }[] => {
    const n = dates.length, step = n <= 10 ? 1 : Math.ceil(n / 8);
    return dates.map((d, i) => ({ t: dayLabel(d), l: ((i + 0.5) / n) * 100, i }))
        .filter((x) => (x.i % step === 0 && n - 1 - x.i >= step / 2) || x.i === n - 1);
};

/* ----------------------------------------------------------- rótulos do eixo */
/**
 * Casas decimais para distinguir marcas separadas por `step` (máx. 2): 25 ⇒ 0 · 2,5 ⇒ 1 · 0,25 ⇒ 2 · 0,1 ⇒ 1 ·
 * 12,3 ⇒ 0 (passos ≥ 10 nunca ganham casa extra).
 */
export const decimalsFor = (step: number): number => {
    const s = Math.abs(step);
    if (!s || !Number.isFinite(s)) return 0;
    let d = Math.max(0, Math.ceil(-Math.log10(s) - 1e-9));
    const scaled = s * Math.pow(10, d);
    if (s < 10 && Math.abs(scaled - Math.round(scaled)) > 1e-6) d += 1;
    return Math.min(2, d);
};

